/**
 * Shared utilities for the debug "Details" modals (session + message).
 *
 * Keeps the common layout, AJAX flow, and copy-to-clipboard behaviour in one
 * place so both detail modals don't duplicate the same code.
 */

export function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

export function formatDetailsValue(value) {
    if (value === null || value === undefined || value === '') {
        return '<span class="text-muted">-</span>';
    }

    if (typeof value === 'object') {
        return `<pre style="white-space: pre-wrap; word-break: break-word; margin: 0;">${escapeHtml(JSON.stringify(value, null, 2))}</pre>`;
    }

    return `<pre style="white-space: pre-wrap; word-break: break-word; margin: 0;">${escapeHtml(String(value))}</pre>`;
}

/**
 * Builds the plain-text to put on the clipboard for a section's value.
 * Mirrors how `formatDetailsValue` renders the value for display.
 */
function serializeDetailsValue(value) {
    if (value === null || value === undefined || value === '') {
        return '';
    }

    if (typeof value === 'object') {
        return JSON.stringify(value, null, 2);
    }

    return String(value);
}

/**
 * Low-level titled <section> wrapper with a per-section copy button.
 * `body` is pre-rendered HTML for display; `copyText` is the plain text
 * copied when the section's button is clicked.
 */
function renderSection(title, body, copyText) {
    return `
        <section>
            <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:8px;">
                <h6 style="margin:0;">${escapeHtml(title)}</h6>
                <button type="button" class="btn btn-sm btn-outline-secondary growtype-chat-details-section-copy" data-copy="${escapeHtml(copyText)}" aria-label="Copy ${escapeHtml(title)}">Copy</button>
            </div>
            ${body ?? ''}
        </section>
    `;
}

/**
 * Renders a titled <section> for a single raw value, with its own copy button.
 */
export function detailsSection(title, value) {
    return renderSection(title, formatDetailsValue(value), serializeDetailsValue(value));
}

/**
 * Renders a titled <section> containing a key/value grid from [label, value] pairs.
 * Copying the section copies every row as `Label: value` lines.
 */
export function detailsGrid(title, pairs) {
    const body = `<div style="display:grid;grid-template-columns:minmax(140px,180px) 1fr;gap:8px 12px;">${pairs.map(([label, value]) => `<strong>${escapeHtml(label)}</strong>${formatDetailsValue(value)}`).join('')}</div>`;
    const copyText = pairs
        .map(([label, value]) => `${label}: ${serializeDetailsValue(value)}`)
        .join('\n');
    return renderSection(title, body, copyText);
}

export function copyToClipboard(text) {
    if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(text);
    }

    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
    return Promise.resolve();
}

function ensureModal(config) {
    let $modal = jQuery(`#${config.modalId}`);

    if ($modal.length) {
        return $modal;
    }

    jQuery('body').append(`
        <div class="modal fade" id="${config.modalId}" tabindex="-1" aria-hidden="true">
            <div class="modal-dialog modal-lg modal-dialog-scrollable">
                <div class="modal-content">
                    <div class="modal-header">
                        <h5 class="modal-title">${config.title}</h5>
                        <button type="button" class="btn btn-sm btn-outline-secondary ms-auto ${config.copyClass}" aria-label="Copy ${config.copyLabel}">
                            <i class="fa-solid fa-copy"></i> Copy
                        </button>
                        <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                    </div>
                    <div class="modal-body">
                        <div class="${config.bodyClass}">Loading...</div>
                    </div>
                </div>
            </div>
        </div>
    `);

    return jQuery(`#${config.modalId}`);
}

function wireCopyButton($modal, config, data) {
    const $copyBtn = $modal.find(`.${config.copyClass}`);
    const copyText = JSON.stringify(data, null, 2);

    $copyBtn.off('click.growtypeChatDetailsCopy').on('click.growtypeChatDetailsCopy', function () {
        const $btn = jQuery(this);
        const originalHtml = $btn.html();

        copyToClipboard(copyText)
            .then(() => {
                $btn.html('<i class="fa-solid fa-check"></i> Copied!');
                window.setTimeout(() => $btn.html(originalHtml), 1500);
            })
            .catch(() => {
                $btn.html('<i class="fa-solid fa-triangle-exclamation"></i> Failed');
                window.setTimeout(() => $btn.html(originalHtml), 1500);
            });
    });
}

/**
 * Wires the per-section copy buttons rendered by `detailsSection`/`detailsGrid`.
 * Each button copies the raw value stored in its `data-copy` attribute.
 */
function wireSectionCopyButtons($body) {
    $body.find('.growtype-chat-details-section-copy').off('click.growtypeChatDetailsSectionCopy').on('click.growtypeChatDetailsSectionCopy', function () {
        const $btn = jQuery(this);
        const copyText = $btn.attr('data-copy') || '';
        const originalHtml = $btn.html();

        copyToClipboard(copyText)
            .then(() => {
                $btn.html('<i class="fa-solid fa-check"></i> Copied!');
                window.setTimeout(() => $btn.html(originalHtml), 1500);
            })
            .catch(() => {
                $btn.html('<i class="fa-solid fa-triangle-exclamation"></i> Failed');
                window.setTimeout(() => $btn.html(originalHtml), 1500);
            });
    });
}

/**
 * Opens a details modal via AJAX and renders the result.
 *
 * config: {
 *   modalId, title, bodyClass, copyClass, copyLabel,
 *   buildRequest: ({$modal, sessionToken}) => object|null   (extra keys merged into POST data; return null to abort)
 *   render: (data) => string
 *   getAction: () => string|null
 *   isEnabled: () => boolean
 *   getSessionToken: ($trigger) => string
 *   renderError: () => string   -- optional, defaults to a generic message
 *   onAbort: (error) => void   -- optional, called when a guard/beforeOpen aborts
 * }
 */
export function openDetailsModal($trigger, config) {
    if (!config.isEnabled() || !config.getAction()) {
        return;
    }

    const sessionToken = config.getSessionToken($trigger);
    if (!sessionToken) {
        config.onAbort?.(new Error('missing session token'));
        return;
    }

    // Build the extra request data before opening so that a missing required
    // value (e.g. message id) aborts without showing a stuck "Loading..." modal.
    const extraData = config.buildRequest($trigger);
    if (!extraData) {
        config.onAbort?.(new Error('missing required request value'));
        return;
    }

    const $modal = ensureModal(config);
    const $body = $modal.find(`.${config.bodyClass}`);
    $body.html('Loading...');
    $modal.modal('show');

    jQuery.ajax({
        url: growtype_chat_ajax.url,
        type: 'POST',
        dataType: 'json',
        data: {
            action: config.getAction(),
            nonce: growtype_chat_ajax.nonce,
            session_token: sessionToken,
            ...extraData,
        },
        success(response) {
            if (!response?.success || !response?.data) {
                $body.html(`<div class="alert alert-danger mb-0">${escapeHtml(response?.data?.message ?? config.renderError?.() ?? 'Unable to load details.')}</div>`);
                return;
            }

            $body.html(config.render(response.data));
            wireCopyButton($modal, config, response.data);
            wireSectionCopyButtons($body);
        },
        error(xhr) {
            const message = xhr?.responseJSON?.data?.message ?? config.renderError?.() ?? 'Unable to load details.';
            $body.html(`<div class="alert alert-danger mb-0">${escapeHtml(message)}</div>`);
        }
    });
}

/**
 * Binds a click trigger (delegated on document) to openDetailsModal.
 */
export function initDetailsTrigger({ selector, event, config, getTrigger }) {
    jQuery(document).off(event).on(event, selector, function (eventHandler) {
        eventHandler.preventDefault();
        openDetailsModal(getTrigger ? getTrigger(jQuery(this)) : jQuery(this), config);
    });
}
